// /settings/focus — tenant focus configuration (NAICS / PSC / set-asides /
// geography / agencies). Source of truth for the personalize worker's
// cold-start bootstrap. Same React component is reused inside the first-login
// modal (apps/terminal/src/components/onboarding/FirstLoginFocusModal.tsx).

import { type TenantFocus, TenantFocusForm } from '@/components/onboarding/TenantFocusForm';
import { ApiError, apiFetch } from '@/lib/api';
import { ObjectHeader } from '@/primitives';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_authed/settings/focus')({
  component: SettingsFocusPage,
});

interface MeWithFocus {
  user_id: string;
  tenant_id: string;
  tenant_name: string;
  email: string | null;
  display_name: string | null;
  role: string;
  focus: TenantFocus | null;
}

function SettingsFocusPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeWithFocus>('/v1/me'),
  });

  const [focus, setFocus] = useState<TenantFocus>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (me.data?.focus) setFocus(me.data.focus);
  }, [me.data]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await apiFetch('/v1/me/focus', { method: 'PATCH', body: focus });
      // Refresh /me so the first-login modal trigger re-evaluates.
      qc.invalidateQueries({ queryKey: ['me'] });
      // Phase D PR-12: trigger on-demand cold-start bootstrap so a focus
      // edit immediately re-ranks the AI rail. Best-effort: the nightly
      // worker reconciles if this fails.
      apiFetch('/v1/personalize/bootstrap', { method: 'POST' })
        .then(() => qc.invalidateQueries({ queryKey: ['ai', 'suggest', 'horizon'] }))
        .catch(() => {
          /* best-effort */
        });
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.code}: ${e.detail ?? ''}`.trim() : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px', maxWidth: 720 }}>
      <ObjectHeader type="OPP" title="FOCUS" sub={me.data?.tenant_name ?? '—'} pills={[]} />
      <div
        style={{
          marginTop: 12,
          fontFamily: 'var(--mono)',
          fontSize: 12,
          color: 'var(--fg-2)',
          lineHeight: 1.55,
        }}
      >
        These settings determine which opportunities your AI rail surfaces while we're learning your
        behaviour. Your firm's NAICS / PSC focus, set-aside certifications, geography, and target
        agencies feed the cold-start vector. We blend in real signals (drafted bids, viewed briefs)
        once you've used the terminal for a few sessions.
      </div>

      <TenantFocusForm value={focus} onChange={setFocus} />

      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn pri"
          style={{ minWidth: 120 }}
        >
          {saving ? 'saving…' : 'SAVE FOCUS'}
        </button>
        {savedAt != null && Date.now() - savedAt < 4_000 && (
          <span className="meta mono grn">saved · the next nightly worker will re-rank</span>
        )}
        {err && <span className="meta mono rd">{err}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => navigate({ to: '/horizon' })} className="btn gh">
          BACK TO HORIZON
        </button>
      </div>
    </div>
  );
}
